import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { fetchAuditQueue, fetchAuditResponseDetail, submitAuditReview } from "@/lib/api/admin";
import type {
  AuditQueueItem,
  AuditResponseDetail,
  AuditReviewSubmission,
  AuditStatusFilter,
} from "@/lib/api/admin";

const VERDICTS: AuditReviewSubmission["verdict"][] = [
  "correct",
  "partial",
  "incorrect",
  "hallucination",
];
const SEVERITIES: AuditReviewSubmission["severity"][] = ["low", "medium", "high"];

export function AuditPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<AuditStatusFilter>("pending");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const queueQuery = useQuery<AuditQueueItem[], Error>({
    queryKey: ["admin", "audit", "queue", statusFilter],
    queryFn: () => fetchAuditQueue(statusFilter, 100),
    refetchInterval: 30_000,
  });

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {/* Queue sidebar */}
      <div className="flex w-80 flex-shrink-0 flex-col border-r border-pantera-100 bg-white">
        <div className="border-b border-pantera-100 px-5 py-5">
          <h2 className="text-[15px] font-bold text-pantera-900">{t("admin.audit.title")}</h2>

          {/* Filter tabs */}
          <div className="mt-3 flex gap-1 rounded-pill border border-pantera-100 bg-pantera-50 p-1 text-xs">
            {(["pending", "reviewed", "all"] as AuditStatusFilter[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setStatusFilter(option)}
                className={`flex-1 rounded-pill px-2 py-1.5 font-semibold transition-colors ${
                  statusFilter === option
                    ? "bg-pantera-700 text-white shadow-sm"
                    : "text-muted hover:text-pantera-900"
                }`}
              >
                {t(`admin.audit.filter.${option}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {queueQuery.isLoading && (
            <p className="px-2 py-3 text-xs text-muted">{t("admin.audit.loading")}</p>
          )}
          {queueQuery.isError && (
            <p className="px-2 py-3 text-xs text-error">
              {t("admin.audit.error", { message: queueQuery.error?.message ?? "" })}
            </p>
          )}
          {queueQuery.data && queueQuery.data.length === 0 && (
            <p className="px-2 py-3 text-xs text-muted">{t("admin.audit.empty")}</p>
          )}

          <ul className="space-y-1.5">
            {(queueQuery.data ?? []).map((item) => (
              <li
                key={item.response_id}
                onClick={() => setSelectedId(item.response_id)}
                className={`cursor-pointer rounded-card border p-3 text-xs transition-colors ${
                  selectedId === item.response_id
                    ? "border-pantera-700 bg-pantera-50 ring-1 ring-pantera-700/30"
                    : "border-pantera-100 bg-white hover:border-pantera-200 hover:bg-pantera-50/50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-semibold text-pantera-900">
                    {item.user_display_name}
                  </span>
                  <StatusBadge status={item.review_status} />
                </div>
                <p className="mt-1.5 line-clamp-2 text-[11px] text-muted">{item.query_text}</p>
                <div className="mt-1.5 flex justify-between text-[10px] text-muted/70">
                  <span className="font-mono">${item.total_cost_usd.toFixed(4)}</span>
                  <span className="font-mono">{item.latency_ms} ms</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Detail panel */}
      <div className="min-w-0 flex-1 overflow-auto bg-pantera-50 p-8">
        {selectedId ? (
          <AuditDetail
            responseId={selectedId}
            onReviewed={() => {
              setSelectedId(null);
              void queueQuery.refetch();
            }}
          />
        ) : (
          <div className="flex h-full min-h-[60vh] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[12px] bg-pantera-100">
                <ClipboardIcon />
              </div>
              <p className="text-sm font-medium text-pantera-900">{t("admin.audit.selectHint")}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: "pending" | "reviewed" }) {
  const { t } = useTranslation();
  const cls =
    status === "pending" ? "bg-accent-400/30 text-accent-600" : "bg-success/10 text-success";
  return (
    <span className={`badge text-[10px] uppercase tracking-wide ${cls}`}>
      {t(`admin.audit.status.${status}`)}
    </span>
  );
}

interface AuditDetailProps {
  responseId: string;
  onReviewed: () => void;
}

function AuditDetail({ responseId, onReviewed }: AuditDetailProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const detailQuery = useQuery<AuditResponseDetail, Error>({
    queryKey: ["admin", "audit", "response", responseId],
    queryFn: () => fetchAuditResponseDetail(responseId),
  });

  const [verdict, setVerdict] = useState<AuditReviewSubmission["verdict"]>("correct");
  const [severity, setSeverity] = useState<AuditReviewSubmission["severity"]>("low");
  const [categoriesInput, setCategoriesInput] = useState("");
  const [comments, setComments] = useState("");

  const submitMutation = useMutation({
    mutationFn: (payload: AuditReviewSubmission) => submitAuditReview(responseId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit"] });
      onReviewed();
    },
  });

  if (detailQuery.isLoading) {
    return <p className="text-sm text-muted">{t("admin.audit.loading")}</p>;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <p className="text-sm text-error">
        {t("admin.audit.error", { message: detailQuery.error?.message ?? "" })}
      </p>
    );
  }

  const detail = detailQuery.data;
  const responseUi = detail.response_ui as { answer_markdown?: string };
  const correctedDefault = detail.review?.corrected_response_json
    ? JSON.stringify(detail.review.corrected_response_json, null, 2)
    : "";

  return (
    <div className="card space-y-5 p-6">
      <header>
        <p className="eyebrow mb-1">
          {t("admin.audit.queryFromUser", { name: detail.user_display_name })}
        </p>
        <p className="text-[15px] font-semibold text-pantera-900">{detail.query_text}</p>
      </header>

      <section>
        <h4 className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
          {t("admin.audit.section.answer")}
        </h4>
        <p className="whitespace-pre-line text-sm text-pantera-900">
          {responseUi.answer_markdown ?? "(empty)"}
        </p>
      </section>

      <section>
        <h4 className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
          {t("admin.audit.section.filters")}
        </h4>
        <pre className="max-h-40 overflow-auto rounded-card border border-pantera-100 bg-pantera-50 p-3 text-[11px] text-pantera-900">
          {JSON.stringify(detail.query_filters, null, 2)}
        </pre>
      </section>

      <section>
        <h4 className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
          {t("admin.audit.section.steps")}
        </h4>
        <ul className="space-y-1 text-xs text-pantera-900">
          {detail.agent_steps.map((step, idx) => {
            const obj = step as { step?: string; duration_ms?: number };
            return (
              <li
                key={idx}
                className="flex justify-between border-b border-pantera-100 pb-1 last:border-b-0"
              >
                <span className="font-mono">{obj.step ?? `step-${idx}`}</span>
                <span className="text-muted">{obj.duration_ms ?? 0} ms</span>
              </li>
            );
          })}
        </ul>
      </section>

      <ReviewForm
        existing={detail.review}
        verdict={verdict}
        setVerdict={setVerdict}
        severity={severity}
        setSeverity={setSeverity}
        categoriesInput={categoriesInput}
        setCategoriesInput={setCategoriesInput}
        comments={comments}
        setComments={setComments}
        defaultCorrected={correctedDefault}
        onSubmit={(corrected) =>
          submitMutation.mutate({
            verdict,
            severity,
            categories: categoriesInput
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean),
            comments: comments || undefined,
            corrected_response: corrected,
          })
        }
        submitting={submitMutation.isPending}
        error={submitMutation.error}
      />
    </div>
  );
}

interface ReviewFormProps {
  existing: AuditResponseDetail["review"];
  verdict: AuditReviewSubmission["verdict"];
  setVerdict: (v: AuditReviewSubmission["verdict"]) => void;
  severity: AuditReviewSubmission["severity"];
  setSeverity: (s: AuditReviewSubmission["severity"]) => void;
  categoriesInput: string;
  setCategoriesInput: (s: string) => void;
  comments: string;
  setComments: (s: string) => void;
  defaultCorrected: string;
  submitting: boolean;
  error: unknown;
  onSubmit: (corrected: Record<string, unknown> | null) => void;
}

function ReviewForm(props: ReviewFormProps) {
  const { t } = useTranslation();
  const [correctedRaw, setCorrectedRaw] = useState(props.defaultCorrected);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    let correctedPayload: Record<string, unknown> | null = null;
    if (correctedRaw.trim().length > 0) {
      try {
        correctedPayload = JSON.parse(correctedRaw);
        setParseError(null);
      } catch (e) {
        setParseError((e as Error).message);
        return;
      }
    }
    props.onSubmit(correctedPayload);
  };

  const fieldCls =
    "mt-1 w-full rounded-card border border-pantera-100 bg-pantera-50 px-3 py-2 text-sm text-pantera-900 outline-none focus:border-pantera-700 focus:ring-2 focus:ring-pantera-700/10 transition";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border-t border-pantera-100 pt-5">
      <h4 className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
        {t("admin.audit.section.review")}
      </h4>

      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs font-semibold text-muted">
          {t("admin.audit.field.verdict")}
          <select
            value={props.verdict}
            onChange={(e) => props.setVerdict(e.target.value as AuditReviewSubmission["verdict"])}
            className={fieldCls}
          >
            {VERDICTS.map((v) => (
              <option key={v} value={v}>
                {t(`admin.audit.verdict.${v}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-semibold text-muted">
          {t("admin.audit.field.severity")}
          <select
            value={props.severity}
            onChange={(e) => props.setSeverity(e.target.value as AuditReviewSubmission["severity"])}
            className={fieldCls}
          >
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {t(`admin.audit.severity.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="block text-xs font-semibold text-muted">
        {t("admin.audit.field.categories")}
        <input
          type="text"
          value={props.categoriesInput}
          onChange={(e) => props.setCategoriesInput(e.target.value)}
          placeholder={t("admin.audit.categoriesPlaceholder")}
          className={fieldCls}
        />
      </label>

      <label className="block text-xs font-semibold text-muted">
        {t("admin.audit.field.comments")}
        <textarea
          value={props.comments}
          onChange={(e) => props.setComments(e.target.value)}
          rows={3}
          className={`${fieldCls} resize-none`}
        />
      </label>

      <label className="block text-xs font-semibold text-muted">
        {t("admin.audit.field.correctedJson")}
        <textarea
          value={correctedRaw}
          onChange={(e) => setCorrectedRaw(e.target.value)}
          rows={6}
          className={`${fieldCls} resize-none font-mono text-[11px]`}
          placeholder='{"answer_markdown": "..."}'
        />
        {parseError && <span className="mt-1 text-[11px] text-error">JSON: {parseError}</span>}
      </label>

      {props.error instanceof Error && (
        <p className="text-xs text-error">
          {t("admin.audit.error", { message: props.error.message })}
        </p>
      )}

      <div className="flex items-center justify-between text-xs text-muted">
        <span>{props.existing ? t("admin.audit.willUpdate") : t("admin.audit.willCreate")}</span>
        <button type="submit" disabled={props.submitting} className="btn-primary px-5 py-2 text-xs">
          {props.submitting ? t("admin.audit.submitting") : t("admin.audit.submit")}
        </button>
      </div>
    </form>
  );
}

function ClipboardIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#3b037d"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
    </svg>
  );
}
